<template>
  <div class="authoring-container">
    <div class="header-container">
      <LogoHeader :handleClick="resetConversation" />
      <AccountControls />
      <FilesGlossaryDialogs />
      <BbToastMessage
        :modelValue="showConversationErrorToast"
        title="Sorry, something went wrong while trying to open this document"
        location="top center"
      />
    </div>

    <div class="content-container">
      <div class="main-container">
        <AuthoringFilters ref="authoringFiltersRef" @unsubscribeConversation="unsubscribeFromAuthConv" />
        <AuthoringSkeletonLoader v-if="!isAuthoringStarted" />

        <div class="authoring-container" v-else>
          <DocumentEditor
            v-if="mjml"
            :html="mjml"
            :forceRerender="forceEditorRerenderCount"
            @saveContent="saveEditorContentStateFromIframe"
            @handleKeydown="handleKeyDown"
            @onUndo="onUndoContent"
            @onRedo="onRedoContent"
          />
          <AuthoringConversation class="authoring-conversation" />
        </div>
      </div>

      <AccountControlPanels />
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useWesley } from '@anthill-modules/wesley-components';

import { SetSelectedValues } from '~/constants/actions';
import { Routes } from '~/constants/routes';

import AccountControlPanels from '~/components/account/AccountControlPanels.vue';
import AccountControls from '~/components/account/AccountControls';
import FilesGlossaryDialogs from '~/components/common/FilesGlossaryDialogs.vue';
import LogoHeader from '~/components/common/LogoHeader';
import AuthoringConversation from '~/components/conversation/authoring/AuthoringConversation';
import AuthoringFilters from '~/components/conversation/authoring/AuthoringFilters.vue';
import AuthoringSkeletonLoader from '~/components/conversation/authoring/AuthoringSkeletonLoader.vue';
import DocumentEditor from '~/components/conversation/authoring/DocumentEditor';

import useAuthoring from '~/composables/useAuthoring';
import useCollections from '~/composables/useCollections';
import useConversations from '~/composables/useConversations';
import createHtmlHistory from '~/composables/createHtmlHistory';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useDocuments from '~/composables/useDocuments';

export default {
  components: {
    AccountControlPanels,
    AccountControls,
    AuthoringConversation,
    AuthoringFilters,
    FilesGlossaryDialogs,
    AuthoringSkeletonLoader,
    DocumentEditor,
    LogoHeader,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.path === Routes.MyCreations) {
        setTimeout(() => {
          vm.$router.push({ path: to.path });
        }, 100);
      } else {
        vm.$router.push({ path: to.path });
      }
    });
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const {
      isAuthoringStarted,
      setIsAuthoringStarted,
      handleAuthoringMetadata
    } = useAuthoring();
    const {
      editorContent,
      getAllTemplates,
      getDesignSystemById,
      getEmailTokens,
      currentTemplate,
      setCurrentTemplate,
      saveHtmlDocumentContentToDb,
      updateReferencesListWithThumbs,
    } = useDocumentEditor();
    const { getAuthoredDocumentById } = useDocuments();
    const {
      createAuthoringConversation,
      getConversationById,
      setCurrentConversation,
      currentConversation,
      setDefaultState,
      subscribeConversation,
      subscribeToConversationNamingChannel,
      unsubscribeConversation,
      unsubscribeFromConversationNamingChannel,
    } = useConversations();
    const {
      currentHtml,
      undo,
      redo,
      setInitialTemplateOnStore,
      saveEditorContentStateFromIframe,
      loadStateFromSession,
    } = createHtmlHistory();
    const { currentCollections, getUserCollections } = useCollections();
    const { sendMessage: sendMessageToDocument, setIframeWindow } = useWesley();

    const mjml = ref('');
    const isEventListenerAttached = ref(false);
    const authoringFiltersRef = ref(null);
    const showConversationErrorToast = ref(false);
    const forceEditorRerenderCount = ref(0);
    const currentRouteName = route.name;
    const currentRouteConversationId = ref(route.params.conversationId);
    const conversationId = computed(() => currentConversation.value.conversationId);

    const unsubscribeFromAuthConv = () => {
      const authConvId = conversationId.value || currentRouteConversationId.value;
      unsubscribeConversation(authConvId, currentRouteName);
    };

    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      if (ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        onUndoContent();
      }

      if (ctrlKey && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        onRedoContent();
      }
    };

    const clearFilters = () => {
      if (authoringFiltersRef.value) {
        authoringFiltersRef.value.clearFilters();
      }
    };

    const onUndoContent = async () => {
      await undo();
      mjml.value = currentHtml.value;
      // force DocumentEditor to rerender
      forceEditorRerenderCount.value += 1;
    };

    const onRedoContent = async () => {
      await redo();
      mjml.value = currentHtml.value;
      // force DocumentEditor to rerender
      forceEditorRerenderCount.value += 1;
    };

    const setAuthConversationMetadata = async (conversation) => {
      if (currentTemplate.value?.id) {
         // need design system config, ex: colors
         const { designSystemId } = conversation;
         const collections = currentCollections.value;
         await handleAuthoringMetadata(conversation, collections);
         if (designSystemId) {
          await getDesignSystemById(designSystemId);
        }
    }
  }
    const handleAuthoringConversation = async (authoredData) => {
      const conversation = await setCurrentConversation(route.params.conversationId);
      if (conversation) {
        subscribeConversation(route.params.conversationId, Routes.Authoring.name);
        setCurrentTemplate({ id: authoredData.id, content: authoredData.content });
        updateReferencesListWithThumbs(authoredData.footnotes || []);
        await store.dispatch(`authoringStore/${SetSelectedValues}`, {
          id: 'design',
          value: conversation.templateId,
        });
        await setAuthConversationMetadata(conversation);
      } else if (!conversation && authoredData.parentId) {
          // duplicate logic here
          const parentConversation = await getConversationById(authoredData.parentId);
          await setCurrentTemplate({ id: parentConversation.templateId , content: authoredData.content, parentId: parentConversation.designSystemId });
          updateReferencesListWithThumbs(authoredData.footnotes || []);
          await store.dispatch(`authoringStore/${SetSelectedValues}`, {
            id: 'design',
            value: parentConversation.templateId,
          });
          await setAuthConversationMetadata(parentConversation);
          await createAuthoringConversation(authoredData.content, authoredData.id);
      } else {
        showConversationErrorToast.value = true;
        router.push({ name: Routes.Authoring.name });
        setTimeout(() => {
          showConversationErrorToast.value = false;
        }, 6000);
      }
    };

    const resetConversation = () => {
      unsubscribeFromAuthConv();
      setIsAuthoringStarted(false);
      setDefaultState();
      clearFilters();
      router.push({ name: Routes.Authoring.name });
    };

        // Add click event listener to detect clicks outside DocumentEditor and not in AuthoringConversation
    const detectClickOutsideDocumentEditor = () => {
      document.addEventListener('click', (event) => {
          const authoringContainer = document.querySelector('.authoring-container');
          const authoringConversation = document.querySelector('.authoring-container .authoring-conversation');
          const documentEditor = document.querySelector('.document-editor');

          if (
            authoringContainer &&
            authoringConversation &&
            documentEditor &&
            authoringContainer.contains(event.target) &&
            !authoringConversation.contains(event.target) &&
            !documentEditor.contains(event.target)
          ) {
            sendMessageToDocument({ method: 'deselectElement' });
          }
        });
    };

    onMounted(async () => {
      window.addEventListener('keydown', handleKeyDown);
      await getUserCollections();
      await getAllTemplates();
      await getEmailTokens();
      subscribeToConversationNamingChannel();

      if (route.params.conversationId) {
        const data = await getAuthoredDocumentById(route.params.conversationId);
        if (data) {
          await handleAuthoringConversation(data);
        }
        detectClickOutsideDocumentEditor();
      }

      if (!isEventListenerAttached.value) {
        window.addEventListener('beforeunload', saveHtmlDocumentContentToDb);
        isEventListenerAttached.value = true;
      }
    });

    // Unsubscribe from the Ably channel when the component is about to be destroyed
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeyDown);
      if (isEventListenerAttached.value) {
        window.removeEventListener('beforeunload', saveHtmlDocumentContentToDb);
        isEventListenerAttached.value = false;
      }

      unsubscribeFromAuthConv();
      unsubscribeFromConversationNamingChannel();
    });
    // Handle when navigating within the app
    onBeforeRouteLeave(async (to, from, next) => {
      await saveHtmlDocumentContentToDb(false);
      setIframeWindow(null);
      next();
    });

    watch([currentTemplate, isAuthoringStarted], async ([newCurrentTemplate, newIsAuthoringStarted]) => {
      if (newIsAuthoringStarted) {
        mjml.value = newCurrentTemplate?.content;
        await setInitialTemplateOnStore(newCurrentTemplate?.content);
        // in case we have something in session storage we set undo redo with the value
        await loadStateFromSession(conversationId.value);
      }
    });

    return {
      forceEditorRerenderCount,
      resetConversation,
      isAuthoringStarted,
      editorContent,
      mjml,
      authoringFiltersRef,
      clearFilters,
      onRedoContent,
      onUndoContent,
      showConversationErrorToast,
      saveEditorContentStateFromIframe,
      handleKeyDown,
      unsubscribeFromAuthConv,
    };
  },
};
</script>

<style lang="scss" scoped>
.authoring-container {
  display: flex;
  background-color: $bb-neutral-0;
  height: 100%;
  width: 100%;
  flex-direction: column;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 27px;
  }

  .main-container {
    padding: $bb-spacing-4 $bb-spacing-15;
    height: 100%;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-12;
    overflow: hidden;
  }

  .content-container,
  .authoring-container {
    display: flex;
    flex-direction: row;
    min-height: 0;
    flex: 1;
    width: 100%;
    justify-content: space-between;
  }

  .authoring-container {
    gap: $bb-spacing-12;
  }
}
</style>
